import React, { useCallback, useEffect, useRef, useState } from 'react';
import OnePagerStyles from './onepagerStyles';
import SiteHeader from './SiteHeader';
import Hero from './Hero';
import Features from './Features';
import StatsBand from './StatsBand';
import RoadmapCta from './RoadmapCta';
import ContactSection from './ContactSection';
import SiteFooter from './SiteFooter';
import AccessModal, { AccessKind } from './AccessModal';
import EnquiryModal, { EnquiryForm, blankEnquiryForm, enquiryErrors } from './EnquiryModal';
import LegalDrawer, { LegalKey } from './LegalDrawer';

type ModalKind = AccessKind | null;

const digits = (v: string) => v.replace(/[^0-9]/g, '');

const OnePager: React.FC = () => {
  const [modal, setModal] = useState<ModalKind>(null);
  const [pin, setPin] = useState('');
  const [wa, setWa] = useState('');
  const [err, setErr] = useState(false);
  const [sent, setSent] = useState(false);
  const [legal, setLegal] = useState<LegalKey | null>(null);
  const [enq, setEnq] = useState(false);
  const [cSeen, setCSeen] = useState(false);
  const [fTried, setFTried] = useState(false);
  const [fSent, setFSent] = useState(false);
  const [f, setF] = useState<EnquiryForm>(blankEnquiryForm);

  const legalBody = useRef<HTMLDivElement>(null);

  /* Escape closes any overlay. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModal(null);
        setLegal(null);
        setEnq(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Contact-card reveal: intersection or 1800ms fallback. */
  useEffect(() => {
    let done = false;
    const reveal = () => {
      if (!done) {
        done = true;
        setCSeen(true);
      }
    };
    const t = setTimeout(reveal, 1800);
    let io: IntersectionObserver | null = null;
    const el = document.getElementById('contact');
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (es) => {
          if (es.some((x) => x.isIntersecting)) {
            reveal();
            if (io) io.disconnect();
          }
        },
        { threshold: 0.05 },
      );
      io.observe(el);
    }
    return () => {
      clearTimeout(t);
      if (io) io.disconnect();
    };
  }, []);

  /* Match the source page chrome while mounted; restore afterwards. */
  useEffect(() => {
    const prevBg = document.body.style.background;
    const prevScroll = document.documentElement.style.scrollBehavior;
    document.body.style.background = '#fff';
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.body.style.background = prevBg;
      document.documentElement.style.scrollBehavior = prevScroll;
    };
  }, []);

  /* ----- access / video modal ----- */
  const openVideo = useCallback(() => setModal('video'), []);
  const closeModal = useCallback(() => setModal(null), []);
  const onPin = useCallback((v: string) => {
    setPin(digits(v).slice(0, 6));
    setErr(false);
  }, []);
  const onWa = useCallback((v: string) => {
    setWa(digits(v).slice(0, 10));
    setErr(false);
  }, []);
  const submitAccess = useCallback(() => {
    const ok = /^[1-9][0-9]{5}$/.test(pin) && /^[6-9][0-9]{9}$/.test(wa);
    if (ok) {
      setSent(true);
      setErr(false);
    } else {
      setErr(true);
    }
  }, [pin, wa]);
  const modalTitle = modal === 'video' ? 'See it in action' : sent ? 'Request received' : 'Request early access';

  /* ----- enquiry modal ----- */
  const openEnquiry = useCallback(() => {
    setEnq(true);
    setFTried(false);
  }, []);
  const closeEnquiry = useCallback(() => {
    setFSent((wasSent) => {
      if (wasSent) {
        setF(blankEnquiryForm());
        return false;
      }
      return wasSent;
    });
    setEnq(false);
  }, []);
  const onField = useCallback((k: keyof EnquiryForm, v: string) => {
    setF((s) => {
      if (k === 'wa') {
        let nv = digits(v);
        if (nv.length > 10 && nv.startsWith('91')) nv = nv.slice(2);
        return { ...s, wa: nv.slice(0, 10) };
      }
      if (k === 'name') return { ...s, name: v.slice(0, 80) };
      if (k === 'school') return { ...s, school: v.slice(0, 120) };
      if (k === 'email') return { ...s, email: v.slice(0, 120) };
      if (k === 'city') return { ...s, city: v.slice(0, 60) };
      return { ...s, [k]: v };
    });
  }, []);
  const onToggleHelp = useCallback((h: string) => {
    setF((s) => ({ ...s, help: s.help.includes(h) ? s.help.filter((x) => x !== h) : [...s.help, h] }));
  }, []);
  const sendContact = useCallback(() => {
    setF((s) => {
      const bad = Object.values(enquiryErrors(s)).some(Boolean);
      if (bad) {
        setFTried(true);
      } else {
        setFTried(false);
        setFSent(true);
      }
      return s;
    });
  }, []);

  /* ----- legal drawer ----- */
  const openLegal = useCallback((k: LegalKey) => {
    setLegal(k);
    requestAnimationFrame(() => {
      if (legalBody.current) legalBody.current.scrollTop = 0;
    });
  }, []);
  const openTerms = useCallback(() => openLegal('terms'), [openLegal]);
  const openPrivacy = useCallback(() => openLegal('privacy'), [openLegal]);
  const closeLegal = useCallback(() => setLegal(null), []);
  const legalContact = useCallback(() => {
    setLegal(null);
    setEnq(true);
    setFSent((wasSent) => {
      if (wasSent) setF(blankEnquiryForm());
      return false;
    });
    setFTried(false);
  }, []);

  return (
    <div className="sm1p">
      <OnePagerStyles />
      <SiteHeader onEarlyAccess={openEnquiry} />
      <Hero onEarlyAccess={openEnquiry} onVideo={openVideo} />
      <Features />
      <StatsBand />
      <RoadmapCta onEarlyAccess={openEnquiry} />
      <ContactSection seen={cSeen} onEnquiry={openEnquiry} />
      <SiteFooter onTerms={openTerms} onPrivacy={openPrivacy} />

      {modal !== null && (
        <AccessModal
          kind={modal}
          title={modalTitle}
          pin={pin}
          wa={wa}
          err={err}
          sent={sent}
          onClose={closeModal}
          onPin={onPin}
          onWa={onWa}
          onSubmit={submitAccess}
        />
      )}

      {enq && (
        <EnquiryModal
          f={f}
          tried={fTried}
          sent={fSent}
          onClose={closeEnquiry}
          onField={onField}
          onToggleHelp={onToggleHelp}
          onSend={sendContact}
          onTerms={openTerms}
          onPrivacy={openPrivacy}
        />
      )}

      {legal !== null && (
        <LegalDrawer
          docKey={legal}
          bodyRef={legalBody}
          onClose={closeLegal}
          onTerms={openTerms}
          onPrivacy={openPrivacy}
          onContact={legalContact}
        />
      )}
    </div>
  );
};

export default OnePager;
